import { useMemo, useState } from "react";
import { Box, Chip, styled } from "@mui/material";
import { Tag, FileText, DollarSign, Hash, Check, Plus } from "lucide-react";
import { FieldCard } from "@components/form/FieldCard";
import { FieldInput, FieldTextarea } from "@components/form/FieldInput";
import { FormFooter } from "@components/form/FormFooter";
import { PickerSheet, PickerOption } from "@components/form/PickerSheet";
import { addTransparency } from "@utils/colorUtils";
import { tokens } from "@styles/tokens";
import { useProductForm } from "../../../hooks/useProductForm";
import { CustomField } from "./CustomField";

interface ProductFormProps {
  handleClose: () => void;
  createFromStockView?: boolean;
}

/**
 * Create/Edit product drawer on the shared forms pattern: core field cards
 * (name, SKU, price, description), the org-defined custom fields, and a tag
 * picker with chips. Logic lives in `useProductForm`; this renders + wires the
 * tag picker. `createFromStockView` is the quick-create from the stock form.
 */
export const ProductForm = ({
  handleClose,
  createFromStockView = false,
}: ProductFormProps) => {
  const {
    isEdit,
    values,
    errors,
    submitting,
    customColumns,
    tagValues,
    selectedTags,
    setField,
    setCustomField,
    addTag,
    removeTag,
    submit,
  } = useProductForm(createFromStockView);

  const [tagPickerOpen, setTagPickerOpen] = useState(false);

  const sortedColumns = useMemo(
    () => [...customColumns].sort((a, b) => a.display_order - b.display_order),
    [customColumns]
  );
  const tagOptions = useMemo<PickerOption[]>(
    () =>
      tagValues
        .filter((t) => !values.tags.includes(t.id))
        .map((t) => ({ value: t.id, label: t.value })),
    [tagValues, values.tags]
  );

  return (
    <Container>
      <Scroll>
        <FieldCard label="Name" error={errors.name}>
          <FieldInput
            value={values.name}
            placeholder="Product name"
            onChange={(e) => setField("name", e.target.value)}
            aria-label="Product name"
          />
        </FieldCard>

        <FieldCard
          label="SKU"
          icon={<Hash size={18} />}
          helper="Defaults to the name if left blank"
          error={errors.sku}
        >
          <FieldInput
            value={values.sku}
            placeholder="SKU"
            onChange={(e) => setField("sku", e.target.value)}
            aria-label="SKU"
          />
        </FieldCard>

        <FieldCard label="Price" icon={<DollarSign size={18} />} error={errors.price}>
          <FieldInput
            type="number"
            inputMode="decimal"
            min={0}
            value={values.price}
            placeholder="0.00"
            onChange={(e) => setField("price", e.target.value)}
            aria-label="Price"
          />
        </FieldCard>

        <FieldCard label="Description" icon={<FileText size={18} />} align="start">
          <FieldTextarea
            value={values.description}
            placeholder="Optional description"
            rows={3}
            onChange={(e) => setField("description", e.target.value)}
            aria-label="Description"
          />
        </FieldCard>

        {sortedColumns.map((column) => (
          <CustomField
            key={column.id}
            column={column}
            value={values.customFields[column.name]}
            error={errors[`custom_${column.name}`]}
            onChange={(v) => setCustomField(column.name, v)}
          />
        ))}

        <FieldCard
          label="Tags"
          icon={<Tag size={18} />}
          onClick={() => setTagPickerOpen(true)}
          trailing={<Plus size={18} />}
        >
          {selectedTags.length ? (
            <TagSummary>{selectedTags.length} selected</TagSummary>
          ) : (
            <Placeholder>Add tags</Placeholder>
          )}
        </FieldCard>

        {selectedTags.length > 0 && (
          <Chips>
            {selectedTags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.value}
                onDelete={() => removeTag(tag.id)}
                sx={{
                  height: 28,
                  fontSize: 14,
                  border: `1px solid ${tag.metadata.color}`,
                  color: tag.metadata.color as string,
                  backgroundColor: addTransparency(
                    tag.metadata.color as string,
                    0.1
                  ),
                }}
              />
            ))}
          </Chips>
        )}
      </Scroll>

      <FormFooter
        onCancel={handleClose}
        onSubmit={submit}
        submitting={submitting}
        submitLabel={isEdit ? "Save Changes" : "Create Product"}
        submitIcon={<Check size={18} />}
      />

      <PickerSheet
        open={tagPickerOpen}
        title="Add Tag"
        searchPlaceholder="Search tags…"
        options={tagOptions}
        onSelect={addTag}
        onClose={() => setTagPickerOpen(false)}
      />
    </Container>
  );
};

const Container = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
});

const Scroll = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(4),
  padding: tokens.space(4),
  backgroundColor: theme.skuey.surfaceAlt,
  "& > *": { flexShrink: 0 },
}));

const Placeholder = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.subtle,
}));

const TagSummary = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.body,
}));

const Chips = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: tokens.space(1.5),
  marginTop: -tokens.space(2),
});
